/* eslint-disable react/jsx-no-bind */
import React, { useState, useRef, useEffect } from "react";
import { Modal, Stack } from "@mui/material";
import { Box } from "@mui/system";
import Image from "next/image";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Close, PlayCircleOutline } from "@mui/icons-material";
import { WistiaPlayer } from "@wistia/wistia-player-react";
import { ColletteIconButton } from "@collette-travel/collette-common-controls";

// Define the type for a single media item
interface MediaItem {
  url: string;
  id: string;
  videoId?: string | null;
  altTag: string;
  thumbnail?: string | null;
}

// Define the props for the TourMediaGalleryModal component
interface TourMediaGalleryModalProps {
  showGallery: {
    show: boolean;
    index: number;
  }; // Object containing show boolean and index number
  setShowGallery: (val: { show: boolean; index: number }) => void; // Function to set the showGallery state

  mediaGallery: MediaItem[]; // Array of MediaItem objects
}

const TourMediaGalleryModal: React.FC<TourMediaGalleryModalProps> = ({
  showGallery,
  setShowGallery,
  mediaGallery
}) => {
  const [imageIndex, setImageIndex] = useState<number>(0); // State to track the current image index
  const [isVideoPlaying, setIsVideoPlaying] = useState<boolean>(false); // State to track if video is playing
  const thumbnailRefs = useRef<Array<HTMLDivElement | null>>([]); // Refs for the thumbnails

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90vw",
    maxWidth: "82rem",
    bgcolor: "background.paper",
    borderRadius: "20px",
    boxShadow: 24,
    p: { xs: "2.4rem", sm: "4rem", md: "4rem 6rem" },
    outline: "none"
  };

  function handleClose() {
    setShowGallery({ show: false, index: 0 });
  }

  // Scroll the selected thumbnail into view
  const handleThumbnailClick = (index: number) => {
    setImageIndex(index);
    setIsVideoPlaying(false); // Reset video playing state when switching
    if (thumbnailRefs.current[index]) {
      thumbnailRefs.current[index].scrollIntoView({
        behavior: "smooth",
        inline: "center"
      });
    }
  };

  useEffect(() => {
    setImageIndex(showGallery?.index);
    setIsVideoPlaying(false); // Reset video playing state when modal opens or index changes
  }, [showGallery?.index]);

  return (
    <Modal
      component="section"
      data-module-name="tour-media-gallery-modal"
      data-track="visibility"
      data-testid="tour-media-gallery-modal"
      open={showGallery?.show}
      onClose={handleClose}
      disableScrollLock
    >
      <Box sx={style}>
        <Box
          textAlign="end"
          sx={{
            position: { xs: "static", sm: "absolute" },
            top: { xs: "1.6rem", md: "2.4rem" },
            right: { xs: "1.6rem", md: "2.4rem" }
          }}
        >
          <ColletteIconButton
            testId="tour-media-gallery-modal-close-button"
            onClick={handleClose}
            sx={{ padding: "0.4rem" }}
          >
            <Close />
          </ColletteIconButton>
        </Box>
        <Stack spacing="24px">
          {/* Main Image */}
          {!mediaGallery[imageIndex]?.videoId ? (
            <Box
              sx={{
                aspectRatio: "4/3",
                width: "100%",
                borderRadius: "20px",
                bgcolor: "grey.500",
                overflow: "hidden",
                position: "relative",
                display: "flex",
                justifyContent: "center",
                alignItems: "center"
              }}
            >
              <Image
                src={mediaGallery[imageIndex]?.url}
                alt="gallery-image"
                fill
                style={{ objectFit: "cover" }}
                priority
                quality={85}
                unoptimized={false}
                sizes="(max-width: 768px) 350px, (max-width: 1024px) 790px, 555px"
                data-testid="media-gallery-modal-main-image"
              />
            </Box>
          ) : (
            <Box
              sx={{
                maxHeight: { xs: "300px", md: "400px", lg: "350px" },
                width: "100%",
                borderRadius: "20px",
                overflow: "hidden",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                position: "relative",
                cursor: "pointer"
              }}
            >
              <WistiaPlayer
                id={`tour-media-gallery-video-${imageIndex}`}
                key={mediaGallery[imageIndex]?.videoId}
                mediaId={mediaGallery[imageIndex]?.videoId || ""}
                playBarControl={false}
                volumeControl={false}
                settingsControl={false}
                playerColor="var(--theme-palette-common-white)"
                roundedPlayer={10}
                fullscreenControl={false}
                playPauseControl={false}
                playPauseNotifier={false}
                controlsVisibleOnLoad={false}
                bigPlayButton={false}
                onPlay={() => setIsVideoPlaying(true)}
                onPause={() => setIsVideoPlaying(false)}
                onEnded={() => setIsVideoPlaying(false)}
                seo
              />
              {!isVideoPlaying && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    zIndex: 1,
                    pointerEvents: "none"
                  }}
                >
                  <PlayCircleOutline
                    sx={{
                      height: "78px",
                      width: "78px",
                      color: "common.white",
                      fontSize: { xs: "48px", md: "64px" },
                      filter: (theme) => `drop-shadow(${theme.shadows[7]})`
                    }}
                  />
                </Box>
              )}
            </Box>
          )}

          {/* Navigation Arrows */}
          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            sx={{ fontSize: "16px", fontWeight: 500 }}
          >
            <ColletteIconButton
              size="small"
              testId="left-arrow-icon"
              onClick={() => handleThumbnailClick(imageIndex > 0 ? imageIndex - 1 : 0)}
              disabled={imageIndex === 0}
            >
              <ChevronLeftIcon />
            </ColletteIconButton>
            <p
              style={{
                fontWeight: 500,
                minWidth: "60px",
                textAlign: "center",
                display: "inline-block"
              }}
              data-testid="tour-media-gallery-modal-pagination"
            >
              {imageIndex + 1} of {mediaGallery.length}
            </p>
            <ColletteIconButton
              size="small"
              testId="right-arrow-icon"
              onClick={() =>
                handleThumbnailClick(
                  imageIndex < mediaGallery.length - 1 ? imageIndex + 1 : imageIndex
                )
              }
              disabled={imageIndex === mediaGallery.length - 1}
            >
              <ChevronRightIcon />
            </ColletteIconButton>
          </Stack>

          {/* Thumbnails */}
          <Stack direction="row" spacing="20px" sx={{ overflowX: "hidden" }}>
            {mediaGallery.map((img, index) => (
              <Box
                data-testid={`thumbnail-box_${index}`}
                data-track="click"
                key={img?.id}
                ref={(el: HTMLDivElement | null) => {
                  thumbnailRefs.current[index] = el;
                }}
                onClick={() => handleThumbnailClick(index)}
                sx={{
                  cursor: "pointer",
                  border: imageIndex === index ? "3px solid " : "none",
                  borderColor: "secondary.dark",
                  borderRadius: "5px",
                  backgroundColor: "background.warm_strong",
                  position: "relative",
                  overflow: "hidden",
                  width: { xs: 80, md: 100 },
                  height: { xs: 80, md: 100 },
                  flexShrink: 0
                }}
              >
                {img?.videoId && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      zIndex: 1,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center"
                    }}
                  >
                    <PlayCircleOutline fontSize="large" sx={{ color: "common.white" }} />
                  </Box>
                )}
                <Image
                  src={img?.thumbnail || img?.url}
                  alt={img?.altTag}
                  fill
                  style={{
                    objectFit: "cover"
                  }}
                  quality={75}
                />
              </Box>
            ))}
          </Stack>
        </Stack>
      </Box>
    </Modal>
  );
};

export default TourMediaGalleryModal;
